/**
 * Progressive enhancements for the Cadastre zoning-plan pages:
 *  - count-up animation for the overview statistics,
 *  - instant client-side filtering and sorting of the region town list.
 *
 * Everything degrades gracefully: without JavaScript the numbers are already rendered
 * and the full town list is visible and ordered newest-first by the server.
 */
export function registerZoningPlanEnhancements(snippet: HTMLElement): void {
    initializeCountUp(snippet);
    initializeRegionList(snippet);
}

function initializeCountUp(snippet: HTMLElement): void {
    snippet.querySelectorAll<HTMLElement>('[data-count-to]').forEach(element => {
        if (element.dataset.countDone === '1') {
            return;
        }

        const target = Number(element.getAttribute('data-count-to'));
        if (! Number.isFinite(target) || target <= 0) {
            return;
        }

        element.dataset.countDone = '1';

        const durationMs = 900;
        const start = performance.now();

        const step = (now: number): void => {
            const progress = Math.min((now - start) / durationMs, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            element.textContent = Math.round(target * eased).toLocaleString('cs-CZ');

            if (progress < 1) {
                requestAnimationFrame(step);
            }
        };

        requestAnimationFrame(step);
    });
}

function initializeRegionList(snippet: HTMLElement): void {
    const list = snippet.querySelector<HTMLElement>('[data-zoning-plan-list]');
    if (list === null || list.dataset.zpInit === '1') {
        return;
    }
    list.dataset.zpInit = '1';

    const filterInput = snippet.querySelector<HTMLInputElement>('[data-zoning-plan-filter]');
    const sortSelect = snippet.querySelector<HTMLSelectElement>('[data-zoning-plan-sort]');
    const groups = Array.from(list.querySelectorAll<HTMLElement>('[data-zoning-plan-group]'));

    const applyFilter = (): void => {
        const needle = (filterInput?.value ?? '').trim().toLowerCase();
        groups.forEach(group => {
            let anyVisible = false;
            group.querySelectorAll<HTMLElement>('.zoning-plan-town-row').forEach(row => {
                const matches = needle === '' || (row.getAttribute('data-name') ?? '').includes(needle);
                row.classList.toggle('d-none', ! matches);
                anyVisible = anyVisible || matches;
            });
            group.classList.toggle('d-none', ! anyVisible);
        });
    };

    const applySort = (): void => {
        const mode = sortSelect?.value ?? 'name';
        groups.forEach(group => {
            const groupList = group.querySelector<HTMLElement>('[data-zoning-plan-group-list]');
            if (groupList === null) {
                return;
            }

            Array.from(groupList.querySelectorAll<HTMLElement>('.zoning-plan-town-row'))
                .sort((first, second) => compareTownRows(first, second, mode))
                .forEach(row => groupList.appendChild(row));
        });
    };

    filterInput?.addEventListener('input', applyFilter);
    sortSelect?.addEventListener('change', applySort);
}

function compareTownRows(first: HTMLElement, second: HTMLElement, mode: string): number {
    if (mode === 'documents') {
        return Number(second.getAttribute('data-documents')) - Number(first.getAttribute('data-documents'));
    }

    if (mode === 'name') {
        return (first.getAttribute('data-name') ?? '').localeCompare(second.getAttribute('data-name') ?? '', 'cs');
    }

    return (second.getAttribute('data-extracted') ?? '').localeCompare(first.getAttribute('data-extracted') ?? '');
}
